/** @format */
import isPropValid from '@emotion/is-prop-valid';
import { Divider } from '@mpb-com/feather/components/atoms/divider';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';

import { PRODUCT_RESULTS_PER_PAGE } from '@toucan/common/services/search/product/constants';

import { formatAsCurrency } from '@toucan/client/global/currency';
import { MarketContext } from '@toucan/client/global/market';
import { setSearchParamsForSearchResults } from '@toucan/client/global/nextjs';

import { ConstrainedWrapper } from '@toucan/client/shared/components/constrained-wrapper';

import { ControlGroup } from '@toucan/client/features/search/components/listing-page/components/control-group';
import { ControlGroupSkeleton } from '@toucan/client/features/search/components/listing-page/components/control-group/skeleton';
import {
    PaginationVariant,
    SearchResults,
    SearchResultsVariant,
} from '@toucan/client/features/search/components/search-results';
import {
    Filter,
    SortByOption,
} from '@toucan/client/features/search/config/types';
import {
    getModelIdFromQuery,
    transformSortByToSortByOption,
} from '@toucan/client/features/search/helpers';
import { productListingHelper } from '@toucan/client/features/search/hooks/product-listing';
import { usePriceModifierGroups } from '@toucan/client/features/search/hooks/use-model-price-modifier-groups';
import { useSearchResultsInfiniteScroll } from '@toucan/client/features/search/hooks/use-search-results-infinite-scroll';

import { SearchFilterMenu } from './components/search-filter-menu';
import { SearchFilterModal } from './components/search-filter-modal';
import { DEFAULT_TEST_ID } from './constants';
import { transformFiltersToSearchParams } from './helpers';
import { useProductFacets } from './hooks/use-product-facets';
import { useProductFilters } from './hooks/use-product-filters';
import { useProductSearchParams } from './hooks/use-product-search-params';
import { useProductSearchResults } from './hooks/use-product-search-results';
import { useProductSortOptions } from './hooks/use-product-sort-options';
import { styles } from './product-search-results.styles';
import { ProductSearchResultsProps } from './types';

export type {
    ProductSearchResultsProps,
    ProductSearchResultsType,
} from './types';

const StyledProductSearchResults = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const ProductSearchResults: FunctionComponent<
    ProductSearchResultsProps
> = ({
    baseQuery = {},
    onCreateAlert,
    banner,
    isLoading = false,
    resultsPerPage = PRODUCT_RESULTS_PER_PAGE,
    'data-testid': testId = DEFAULT_TEST_ID,
    ...otherProps
}: ProductSearchResultsProps) => {
    const router = useRouter();
    const { activeMarket } = useContext(MarketContext);
    const [isMobileSearchFilterOpen, setIsMobileSearchFilterOpen] =
        useState<boolean>(false);

    const modelId = getModelIdFromQuery(baseQuery.filterQuery ?? {});
    const { data: modifierGroups } = usePriceModifierGroups(
        modelId,
        activeMarket.code
    );

    const searchParams = useProductSearchParams();
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    const { pageIndex, showAll, filterQuery, variants, sort } = searchParams;

    const { sortOptions, defaultSortOption } = useProductSortOptions();
    const sortByOption = transformSortByToSortByOption({
        sort,
        sortOptions,
        defaultSortOption,
    });

    const { searchResults, numResultSkeletons } = useProductSearchResults({
        baseQuery,
        searchParams,
        resultsPerPage,
        sortedBy: sortByOption.value,
        modifierGroups,
    });

    const { facets, totalInitialResults } = useProductFacets(baseQuery);

    const { filters, filtersAppliedCount } = useProductFilters({
        facets,
        filterQuery,
        selectedVariants: variants,
        baseQuery,
        modifierGroups,
    });

    const formatPrice = useMemo(() => {
        return formatAsCurrency(activeMarket.iso, activeMarket.currency);
    }, [activeMarket.iso, activeMarket.currency]);

    const { getListingCards } = productListingHelper(formatPrice, false);

    const triggerRef = useSearchResultsInfiniteScroll({
        showAll,
        totalResults: searchResults.totalResults,
        resultsPerPage,
        pageIndex,
        onNextPage: handleNextPage,
    });

    function handleSortByChange(selectedSortByOption: SortByOption): void {
        const sortParam =
            selectedSortByOption === defaultSortOption
                ? undefined
                : `${selectedSortByOption.facet}:${selectedSortByOption.direction}`;

        const pageReset = showAll ? { page: undefined } : {};

        setSearchParamsForSearchResults(router, {
            sort: sortParam,
            ...pageReset,
        });
    }

    function handleSearchFilterUpdate(selectedFilters: Filter[]): void {
        setSearchParamsForSearchResults(router, {
            ...transformFiltersToSearchParams(selectedFilters),
            page: undefined,
        });
    }

    function handleMobileFilterApply(selectedFilters: Filter[]): void {
        handleSearchFilterUpdate(selectedFilters);
        setIsMobileSearchFilterOpen(false);
    }

    function handleMobileFilterClose(): void {
        setIsMobileSearchFilterOpen(false);
    }

    function handlePaginationUpdate(nextPageIndex: number): void {
        const page = nextPageIndex === 0 ? undefined : nextPageIndex + 1;

        setSearchParamsForSearchResults(router, { page }, false);
    }

    function handleNextPage(page: number): void {
        setSearchParamsForSearchResults(router, { page }, false);
    }

    function handleShowAllResults(): void {
        setSearchParamsForSearchResults(router, {
            showAll: true,
            page: undefined,
        });
    }

    const listingCards = getListingCards(searchResults, numResultSkeletons);
    const hasLessThanTwoResults = totalInitialResults < 2;
    const desktopFilterMenuClassName = `desktop-filters ${
        hasLessThanTwoResults ? 'desktop-filters--hidden' : ''
    }`.trim();

    return (
        <StyledProductSearchResults
            isLoading={isLoading}
            data-testid={testId}
            {...otherProps}
        >
            <div
                className="small-tablet-filters"
                data-testid={`${testId}__filters__small-tablet`}
            >
                <SearchFilterModal
                    isOpen={isMobileSearchFilterOpen}
                    resultsCount={searchResults.totalResults}
                    filters={filters}
                    onApply={handleMobileFilterApply}
                    onClose={handleMobileFilterClose}
                    onCancel={handleMobileFilterClose}
                    baseQuery={baseQuery}
                    totalInitialResults={totalInitialResults}
                />
            </div>
            <div className="control-group">
                <ControlGroupSkeleton
                    data-testid={`${testId}__control-group-skeleton`}
                    className="control-group-skeleton"
                />
                <ControlGroup
                    data-testid={`${testId}__control-group`}
                    className="control-group-content"
                    options={sortOptions}
                    selectedOption={sortByOption}
                    onSortByChange={handleSortByChange}
                    setSearchFilterGroupMobileOpen={setIsMobileSearchFilterOpen}
                    filtersAppliedCount={filtersAppliedCount}
                />
            </div>
            <Divider className="divider" />

            <ConstrainedWrapper
                as="section"
                className={desktopFilterMenuClassName}
                data-testid={`${testId}__filters__desktop`}
            >
                <SearchFilterMenu
                    filters={filters}
                    filtersAppliedCount={filtersAppliedCount}
                    onApply={handleSearchFilterUpdate}
                    baseQuery={baseQuery}
                    totalInitialResults={totalInitialResults}
                />
            </ConstrainedWrapper>

            <SearchResults
                isLoading={isLoading}
                paginationVariant={PaginationVariant.VIEW_MORE}
                resultsInView={listingCards}
                resultsInViewCount={listingCards.length}
                displayAllResults={showAll}
                totalResultsCount={searchResults.totalResults}
                sortBy={sortByOption}
                page={pageIndex}
                variant={SearchResultsVariant.PRODUCT}
                onCreateAlert={onCreateAlert}
                onPaginationClick={handlePaginationUpdate}
                onSeeAllClick={handleShowAllResults}
                sortByOptions={sortOptions}
                onSortByChange={handleSortByChange}
                data-testid={`${testId}__search-results`}
                resultsPerPage={resultsPerPage}
            />
            <div ref={triggerRef} />
            {banner}
        </StyledProductSearchResults>
    );
};
